/**
 * A panel that slides in from the right, over the screen it belongs to.
 *
 * WHY A DRAWER AND NOT THE PAGE ITSELF
 *
 * The Reconcile screen answers two questions that want opposite shapes. "Which
 * months have I not done" is a table you read down, and it wants the width of
 * the screen. "Reconcile THIS month" is a form with three balances and two
 * lists, and it is about one month at a time. Stacked, the second pushed the
 * first below the fold, so the screen opened on a form for a month nobody had
 * chosen yet -- and the table that says what to work on was the thing you had
 * to scroll to find.
 *
 * WHY NOT THE MODAL WE ALREADY HAVE. `ReceiptOverlay` is centred and sized to a
 * document; this is a work surface beside the table, and the table stays
 * readable at the left while it is open. Same backdrop, same Escape, same focus
 * behaviour -- the parts that are about being a dialog are shared, only the
 * geometry differs.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO: trap focus. Neither does `ReceiptOverlay`,
 * and adding it here alone would make the two dialogs on this screen behave
 * differently from each other. Escape closes, focus starts on Close.
 */

import { useEffect, useRef, type ReactNode } from 'react';

export function SideDrawer({
  label,
  onClose,
  children,
}: {
  /** Names the dialog for a screen reader; the visible heading is the caller's. */
  label: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const closeRef = useRef<HTMLButtonElement | null>(null);

  /*
   * Escape closes, and the listener is on the window rather than on the panel,
   * for `ReceiptOverlay`'s reason: this drawer can itself open the statement
   * overlay, whose iframe is a separate document on a separate origin, and a
   * key pressed in there reaches no React handler of ours. A window listener
   * fires wherever focus is in our own page.
   */
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Focus lands on Close, so Escape is not the only way out by keyboard.
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  return (
    <div
      className="overlay-backdrop drawer-backdrop"
      // A click on the backdrop closes; a click inside must not. Checked
      // against the event's own target rather than with stopPropagation on the
      // panel, which would also swallow clicks the panel's controls need.
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="drawer-panel" role="dialog" aria-modal="true" aria-label={label}>
        <header className="row drawer-head">
          <h2>{label}</h2>
          <button type="button" className="link" ref={closeRef} onClick={onClose}>
            Close
          </button>
        </header>
        <div className="drawer-body">{children}</div>
      </div>
    </div>
  );
}
